import { WelcomeVideoPlayer } from "@/components/WelcomeVideo";
import React, { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API, formatApiError, avatarUrl } from "@/lib/api";
import { NameStep, HiStep, PasswordStep, CodeStep, StepButton as PrimaryButton } from "@/components/onboarding/InviteSteps";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Camera, Loader2, Rocket, Cookie } from "lucide-react";

/* Full-screen, unskippable onboarding with a warp-speed transition between steps. */
const BASE_STEPS = ["welcome", "job", "photo", "snack", "done"];
const INVITE_STEPS = ["name", "hi", "password", "code", "job", "photo", "snack", "done"];

export function Onboarding({ user: initialUser, orgName, onDone, showVideo = false, invite = null }) {
  const steps = invite ? INVITE_STEPS : BASE_STEPS;
  const [step, setStep] = useState(0);
  const cur = steps[step];
  const [user, setUser] = useState(initialUser || null);
  const [http, setHttp] = useState(() => api);
  const [first, setFirst] = useState(invite?.first_name || "");
  const [last, setLast] = useState(invite?.last_name || "");
  const [accepted, setAccepted] = useState(null);
  const [warping, setWarping] = useState(false);
  const [jobTitle, setJobTitle] = useState(user?.job_title || "");
  const [snack, setSnack] = useState(user?.favourite_snack || "");
  const [hasPhoto, setHasPhoto] = useState(user?.has_avatar || false);
  const [photoVersion, setPhotoVersion] = useState(user?.avatar_version || null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const fileRef = useRef(null);

  const firstName = (invite ? first.trim() : "") || user?.first_name || (user?.name || "there").split(" ")[0];
  const brand = invite?.brand;
  const stars = useMemo(
    () => Array.from({ length: 90 }).map(() => ({
      angle: Math.random() * Math.PI * 2,
      dist: 20 + Math.random() * 60,
      size: 0.5 + Math.random() * 2,
      delay: Math.random() * 2,
      dur: 1.4 + Math.random() * 2.6,
    })),
    []
  );

  const go = (next) => {
    if (warping) return;
    setWarping(true);
    setTimeout(() => { setStep(typeof next === "string" ? steps.indexOf(next) : next); }, 520);
    setTimeout(() => { setWarping(false); }, 1040);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be 5MB or smaller"); return; }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasPhoto(true);
      setPhotoVersion(data.avatar_version);
      toast.success("Looking good!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await http.post("/profile/complete-onboarding", { job_title: jobTitle, favourite_snack: snack });
      if (invite) { window.location.href = accepted?.login_url || "/"; return; }
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setFinishing(false);
    }
  };

  const photoSrc = preview || (hasPhoto && user?.id ? avatarUrl(user.id, photoVersion) : null);
  const photoInitials = (`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || firstName).split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const stepVariants = {
    enter: { opacity: 0, scale: 1.5, filter: "blur(14px)", y: 30 },
    center: { opacity: 1, scale: 1, filter: "blur(0px)", y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
    exit: { opacity: 0, scale: 0.4, filter: "blur(20px)", transition: { duration: 0.5, ease: [0.7, 0, 0.84, 0] } },
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden bg-[#06060C] flex items-center justify-center" data-testid="onboarding-overlay">
      {/* violet nebula glow */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 50%, rgba(114,63,255,0.22) 0%, rgba(6,6,12,0) 55%), radial-gradient(80% 70% at 80% 20%, rgba(178,255,89,0.10) 0%, rgba(6,6,12,0) 50%)" }} />

      {/* warp starfield */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {stars.map((s, i) => {
          const x = Math.cos(s.angle);
          const y = Math.sin(s.angle);
          return (
            <motion.span
              key={i}
              className="absolute rounded-full bg-white"
              style={{ width: s.size, height: s.size, left: "50%", top: "50%" }}
              animate={{
                x: [x * s.dist, x * (warping ? 1600 : 620)],
                y: [y * s.dist, y * (warping ? 1600 : 620)],
                scaleY: warping ? [1, 26] : [1, 8],
                scaleX: warping ? [1, 3] : 1,
                opacity: [0, 0.9, 0],
              }}
              transition={{ duration: warping ? 0.7 : s.dur, repeat: Infinity, delay: s.delay, ease: "easeIn" }}
            />
          );
        })}
      </div>

      {/* warp flash */}
      <AnimatePresence>
        {warping && (
          <motion.div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#723FFF]/0 via-[#723FFF]/10 to-white/20"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
          />
        )}
      </AnimatePresence>

      {/* progress dots */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {steps.map((_, i) => (
          <span key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i === step ? "w-8 bg-[#B2FF59]" : i < step ? "w-1.5 bg-white/70" : "w-1.5 bg-white/20"}`} />
        ))}
      </div>

      {brand && (
        <div data-testid="onboarding-brand" className="absolute top-6 left-6 sm:left-8 z-10 flex items-center gap-2.5">
          {brand.logo_url ? <img src={`${API}${brand.logo_url}`} alt={brand.name} className="h-9 w-9 rounded-xl object-cover bg-white" /> : <img src="/shuttle-logo.png" alt={brand.name} className="h-9 w-9 object-contain invert" />}
          <span className="text-white/85 font-display font-semibold text-sm">{brand.name}</span>
        </div>
      )}

      {/* content */}
      <div className={`relative z-10 w-full ${showVideo && cur === "done" ? "max-w-3xl" : "max-w-xl"} px-6 text-center transition-[max-width] duration-500`}>
        <AnimatePresence mode="wait">
          {!warping && (
            <motion.div key={step} variants={stepVariants} initial="enter" animate="center" exit="exit">
              {cur === "name" && <NameStep first={first} last={last} setFirst={setFirst} setLast={setLast} onNext={() => go("hi")} />}
              {cur === "hi" && <HiStep first={firstName} onNext={() => go("password")} />}
              {cur === "password" && <PasswordStep invite={invite} first={first} last={last} onSaved={(d) => { setAccepted(d); go("code"); }} />}
              {cur === "code" && accepted && <CodeStep accepted={accepted} setAccepted={setAccepted} onVerified={(client, u) => { setHttp(() => client); setUser(u); go("job"); }} />}

              {cur === "welcome" && (
                <div data-testid="onboarding-step-welcome">
                  <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">Hi {firstName} <span className="inline-block">👋</span></h1>
                  <p className="mt-4 font-display text-2xl sm:text-3xl text-white/70">Let's get you Mission ready.</p>
                  <PrimaryButton onClick={() => go("job")} testid="onboarding-start">Let's go <ArrowRight className="h-4 w-4 ml-1.5" /></PrimaryButton>
                </div>
              )}

              {cur === "job" && (
                <div data-testid="onboarding-step-job">
                  <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">What do you do at {orgName}?</h2>
                  <div className="mt-8 max-w-sm mx-auto">
                    <Input
                      autoFocus data-testid="onboarding-job-input"
                      value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && jobTitle.trim() && go("photo")}
                      placeholder="What's your job title"
                      className="h-14 text-center text-lg bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-2xl focus-visible:ring-[#B2FF59]"
                    />
                  </div>
                  <PrimaryButton onClick={() => go("photo")} disabled={!jobTitle.trim()} testid="onboarding-job-next">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></PrimaryButton>
                </div>
              )}

              {cur === "photo" && (
                <div data-testid="onboarding-step-photo">
                  <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">Let's upload a photo</h2>
                  <p className="mt-3 text-white/60 text-lg">so the team can put a face to the name.</p>
                  <div className="mt-8 flex justify-center">
                    <button data-testid="onboarding-photo-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="group relative h-36 w-36 rounded-full overflow-hidden border-2 border-white/20 bg-white/5 flex items-center justify-center text-4xl font-semibold text-white hover:border-[#B2FF59] transition-colors">
                      {photoSrc ? <img src={photoSrc} alt="" className="h-full w-full object-cover" /> : photoInitials}
                      <span className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-white" /> : <Camera className="h-6 w-6 text-white" />}
                      </span>
                    </button>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  </div>
                  <div className="mt-8 flex items-center justify-center gap-3">
                    <button data-testid="onboarding-photo-skip" onClick={() => go("snack")} className="text-white/50 hover:text-white/80 text-sm transition-colors">Skip for now</button>
                    <PrimaryButton onClick={() => go("snack")} inline testid="onboarding-photo-next">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></PrimaryButton>
                  </div>
                </div>
              )}

              {cur === "snack" && (
                <div data-testid="onboarding-step-snack">
                  <Cookie className="h-10 w-10 text-[#B2FF59] mx-auto mb-4" />
                  <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">Finally, what's your favourite snack in the world?</h2>
                  <div className="mt-8 max-w-sm mx-auto">
                    <Input
                      autoFocus data-testid="onboarding-snack-input"
                      value={snack} onChange={(e) => setSnack(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && snack.trim() && go("done")}
                      placeholder="We love Tim Tams at Shuttle."
                      className="h-14 text-center text-lg bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-2xl focus-visible:ring-[#B2FF59]"
                    />
                  </div>
                  <PrimaryButton onClick={() => go("done")} disabled={!snack.trim()} testid="onboarding-snack-next">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></PrimaryButton>
                </div>
              )}

              {cur === "done" && (
                <div data-testid="onboarding-step-done">
                  <CelebrationBurst />
                  <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white">You're all set! <span className="inline-block">🧑‍🚀</span></h1>
                  {showVideo && <div className="mt-6 mb-2"><p className="text-sm text-white/70 mb-3">Before you jump in, here's a one-minute tour of Mission Control.</p><WelcomeVideoPlayer autoPlay /></div>}
                  <PrimaryButton onClick={finish} disabled={finishing} testid="onboarding-finish">
                    {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="h-4 w-4 mr-1.5" /> Go to Mission Control</>}
                  </PrimaryButton>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function CelebrationBurst() {
  const bits = useMemo(() => Array.from({ length: 28 }).map((_, i) => ({
    angle: (i / 28) * Math.PI * 2,
    color: ["#B2FF59", "#723FFF", "#ffffff", "#F9C509"][i % 4],
    dist: 90 + Math.random() * 120,
    size: 5 + Math.random() * 7,
  })), []);
  return (
    <div className="relative h-24 flex items-center justify-center mb-2">
      {bits.map((b, i) => (
        <motion.span key={i} className="absolute rounded-sm" style={{ width: b.size, height: b.size, backgroundColor: b.color }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{ x: Math.cos(b.angle) * b.dist, y: Math.sin(b.angle) * b.dist, opacity: 0, scale: 0.4, rotate: 220 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.05 }}
        />
      ))}
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 240, damping: 12 }} className="h-16 w-16 rounded-full bg-[#B2FF59]/20 border border-[#B2FF59]/40 flex items-center justify-center">
        <Rocket className="h-7 w-7 text-[#B2FF59]" />
      </motion.div>
    </div>
  );
}
